import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './Modal';
import { User, Channel, ChannelMember, OnlineUser, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { IS_COARSE_POINTER } from '../utils/pointer';
import { channelService } from '../services/channelService';
import { useAffiliateUserSearch } from '../hooks/useAffiliateUserSearch';
import { UserPickerRow } from './UserPickerRow';
import {
  Search,
  X,
  Loader2,
  UserPlus,
  Building2,
  Building,
} from 'lucide-react';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  channel: Channel;
  currentUser: User;
  onlineUsers: OnlineUser[];
  existingMemberIds?: string[];
  onMembersInvited?: (members: Array<{ id: string; name: string }>) => void;
}

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  channel,
  currentUser,
  onlineUsers,
  existingMemberIds = [],
  onMembersInvited,
}) => {
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // existingMemberIds를 ref로 저장하여 의존성 문제 해결
  const existingMemberIdsRef = useRef(existingMemberIds);
  existingMemberIdsRef.current = existingMemberIds;

  // 계열사별 사용자 검색 훅
  const {
    affiliates,
    selectedAffiliateId,
    setSelectedAffiliateId,
    showAffiliateSelector,
    filteredUsers,
    selectedUsers,
    usersByDepartment,
    allUsers,
    loading,
    error: loadError,
    searchQuery,
    setSearchQuery,
    toggleUser,
    removeUser,
    reset,
  } = useAffiliateUserSearch({
    isActive: isOpen,
    currentUserId: currentUser.id,
    excludeUserIds: existingMemberIdsRef.current,
  });

  // 모달 닫을 때 초기화
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      reset();
    }
  }, [isOpen, reset]);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // 검색 중에 사람을 고르면 검색어를 비운다 — 다음 사람을 찾으려고 지우고 다시 칠 필요가 없게.
  // 터치 기기는 행을 누를 때 키보드가 내려가므로 다시 포커스하지 않는다(닫혔다 열리며 화면이 출렁인다).
  // 체크 해제는 검색어를 유지한다(잘못 누른 것을 고치는 중). 채팅방 개설 창과 같은 규칙
  const handlePick = (userId: string | number) => {
    const picking = !filteredUsers.find((u) => String(u.id) === String(userId))?.selected;
    toggleUser(userId);
    if (!picking || !searchQuery) return;
    setSearchQuery('');
    if (!IS_COARSE_POINTER) searchInputRef.current?.focus();
  };

  // 멤버 초대
  const handleInvite = async () => {
    if (selectedUsers.length === 0) {
      setError('최소 1명의 멤버를 선택해주세요.');
      return;
    }

    setInviting(true);
    setError(null);

    try {
      const members = selectedUsers.map((u) => ({
        id: String(u.id),
        name: u.username,
      }));

      await channelService.addMembers(channel.id, members);

      // 초대 통지·시스템 메시지는 서버(POST /api/channels/:id/members)가 직접 보낸다.
      // 여기서 소켓으로 보강하던 방식은 소켓이 끊겨 있으면 조용히 유실됐다.

      onMembersInvited?.(members);
      onClose();
    } catch (err) {
      console.error('Failed to invite members:', err);
      setError('멤버 초대에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setInviting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="멤버 초대"
      size="md"
      // 사용자 목록이 자체 스크롤을 갖는다 → 본문 스크롤을 끄고 목록에 높이를 넘긴다
      bodyFlex
      footer={
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            {selectedUsers.length}명 선택됨
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={inviting}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              취소
            </button>
            <button
              onClick={handleInvite}
              disabled={inviting || selectedUsers.length === 0}
              className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {inviting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  초대 중...
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  초대하기
                </>
              )}
            </button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4 flex-auto min-h-0">
        {/* 에러 메시지 */}
        {error && (
          <div className="p-3 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 text-sm">
            {error}
          </div>
        )}

        {/* 선택된 멤버 태그 */}
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 border border-slate-100 rounded-lg max-h-24 overflow-y-auto">
            {selectedUsers.map((user) => (
              <span
                key={user.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-50 text-brand-600 rounded-full text-sm"
              >
                <img
                  src={resolveAvatarUrl(user.avatar)} onError={handleAvatarError}
                  alt=""
                  className="w-4 h-4 rounded-full"
                />
                {user.username}
                <button
                  onClick={() => removeUser(user.id)}
                  className="hover:bg-brand-100 rounded-full p-0.5 transition-colors"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* 계열사 선택 드롭다운 */}
        {showAffiliateSelector && (
          <div className="relative">
            <Building
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <select aria-label="초대할 계열사"
              value={selectedAffiliateId}
              onChange={(e) => setSelectedAffiliateId(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400 transition-colors appearance-none cursor-pointer"
            >
              {affiliates.map((aff) => (
                <option key={aff.id} value={aff.id}>
                  {aff.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 멤버 검색 */}
        <div className="relative">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input aria-label="이름 또는 부서로 검색"
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="이름 또는 부서로 검색"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400 transition-colors"
          />
        </div>

        {/* 사용자 목록 로드 에러 */}
        {loadError && (
          <div className="px-3 py-2 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 text-sm">
            {loadError}
          </div>
        )}

        {/* 사용자 목록 */}
        <div className="border border-slate-200 rounded-lg overflow-hidden flex-auto min-h-0 flex flex-col">
          <div className="flex-auto min-h-0 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 size={24} className="animate-spin mr-2" />
                불러오는 중...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                <UserPlus size={32} className="mb-2 opacity-50" />
                <p>{loadError ? '목록을 불러오지 못했습니다' : allUsers.length === 0 ? '초대할 수 있는 멤버가 없습니다' : '검색 결과가 없습니다'}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {usersByDepartment.map(([deptName, users]) => (
                  <div key={deptName}>
                    {/* 부서 헤더 */}
                    <div className="sticky top-0 flex items-center px-4 py-2 bg-white/90 backdrop-blur-sm border-b border-slate-100">
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Building2 size={14} />
                        {deptName}
                        <span className="text-xs opacity-60">
                          ({users.length})
                        </span>
                      </div>
                    </div>

                    {/* 부서원 목록 */}
                    {users.map((user) => (
                      <UserPickerRow
                        key={user.id}
                        user={user}
                        onlineUsers={onlineUsers}
                        onToggle={handlePick}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
